import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { ReactCompareSlider, ReactCompareSliderImage } from "react-compare-slider";
import { useTranslation } from "react-i18next";
import BriaVideo from "../../BriaVideo/BriaVideo";
import BriaImage from "../../Galleries/BriaImage";
import FullScreenImageOverlay from "../Overlay/FullScreenImageOverlay";
import styles from "./FullScreenImage.module.scss";

export type FullScreenImageType = {
	src: string;
	openCompare?: boolean;
	compareTo?: string;
	compareToLabel?: string;
	compareLabel?: string;
	fileName?: string;
	format?: "png" | "svg";
	hide?: boolean;
	is_favorite?: boolean;
	type?: string;
};

type FullScreenImageProps = {
	image: FullScreenImageType;
	index: number;
	displayMode: "arrows" | "scroll";
	displayedImages: FullScreenImageType[];
	setDisplayedImages: (images: FullScreenImageType[]) => void;
	onZoom?: (zoomed: boolean) => void;
	hide?: boolean;
	onDownload?: () => void;
	onCompare?: () => void;
	hideDownloadIcon?: boolean;
	hideDeleteIcon?: boolean;
	hideFavoriteIcon?: boolean;
	displayHideIcon?: boolean;
	hidec2paIcon?: boolean;
	hideApiReferenceButton?: boolean;
	hideFeedbackButtons?: boolean;
};

const FullScreenImage = ({
	image,
	index,
	displayMode,
	displayedImages,
	setDisplayedImages,
	onZoom,
	hide,
	onDownload,
	onCompare,
	hideDownloadIcon,
	hideDeleteIcon,
	hideFavoriteIcon,
	displayHideIcon = true,
	hidec2paIcon,
	hideApiReferenceButton,
	hideFeedbackButtons,
}: FullScreenImageProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "briaImage.fullScreenView" });
	const [zoomSrc, setZoomSrc] = useState<string>();

	const onImageZoom = (zoomed: boolean, zoomSrc: string) => {
		if (zoomed) {
			setZoomSrc(zoomSrc);
		} else {
			setZoomSrc(undefined);
		}
		onZoom && onZoom(zoomed);
	};

	if (image.openCompare && image.compareTo) {
		return (
			<Box
				className={clsx(styles.viewer, {
					[styles.hidden]: image.hide,
					[styles.hiddenVisibility]: hide,
				})}
				onClick={(e) => e.stopPropagation()}
			>
				<Box className={styles.compareSliderContainer}>
					<ReactCompareSlider
						className={styles.compareSlider}
						keyboardIncrement={0}
						itemOne={
							<ReactCompareSliderImage
								src={image.compareTo}
								alt={image.compareToLabel ?? t("compareToLabel")}
							/>
						}
						itemTwo={
							<ReactCompareSliderImage src={image.src} alt={image.compareLabel ?? t("compareLabel")} />
						}
					/>
					<Box className={styles.compareLabels}>
						<Typography>{image.compareToLabel ?? t("compareToLabel")}</Typography>
						<Typography>{image.compareLabel ?? t("compareLabel")}</Typography>
					</Box>
				</Box>
			</Box>
		);
	}

	// Normal mode: single image view
	return (
		<Box
			className={clsx(styles.viewer, {
				[styles.hidden]: image.hide,
				[styles.hiddenVisibility]: hide,
			})}
		>
			<Box
				className={clsx(styles.imageContainer, {
					[styles.hiddenVisibility]: !!zoomSrc && zoomSrc !== "original",
				})}
				onClick={(e) => image.type === "video" && e.stopPropagation()}
			>
				{image.type === "video" ? (
					<BriaVideo videoSrc={image.src} className={styles.video} replay />
				) : (
					<BriaImage
						url={image.src}
						className={styles.image}
						zoomable
						onZoom={(zoomed) => {
							onImageZoom(zoomed, "original");
						}}
						displayOverlay={zoomSrc ? "off" : undefined}
						ImageOverlay={
							<FullScreenImageOverlay
								{...{ image, index, displayMode, displayedImages, setDisplayedImages }}
								onDownload={onDownload}
								onCompare={onCompare}
								hideDownloadIcon={hideDownloadIcon}
								displayHideIcon={displayHideIcon}
								hideApiReferenceButton={hideApiReferenceButton}
								hideFeedbackButtons={hideFeedbackButtons}
							/>
						}
						hideFullScreenButton
						onDownload={onDownload}
						{...image}
						image={image}
						hideDownloadIcon={hideDownloadIcon}
						hideDeleteIcon={hideDeleteIcon}
						hideFavoriteIcon={hideFavoriteIcon}
						hidec2paIcon={hidec2paIcon}
						actionsClassName={styles.actionsWrapper}
						aspectRatio="auto"
					/>
				)}
			</Box>
		</Box>
	);
};
export default observer(FullScreenImage);
